import { Button } from '@headlessui/react';
import { ClassValue } from 'clsx';
import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Modal } from '@/components/base';
import Icon, { IconName } from '@/components/base/Icon';
import { ModalProps } from '@/components/base/Modal/Modal';
import ErrorModal from '@/components/ErrorModal';
import RevampErrorModal from '@/components/RevampErrorModal';
import { useDisclosure } from '@/hooks/useDisclosure';
import { ActionType, ErrorLogData } from '@/interfaces/logging';
import { noop } from '@/utils/noop';

interface ContextType {
  isOpen: boolean;
  data: ModalData | undefined;
  openModal: (data?: ModalData) => void;
  closeModal: () => void;
}

export enum ModalType {
  ERROR = 'error',
  /** Same payload as ERROR, rendered in the revamp design. Scoped to the login/enrollment auth
   *  surfaces so the ~35 call sites on ERROR keep the legacy look. */
  ERROR_REVAMP = 'error-revamp',
  CUSTOM = 'custom',
}

type ModalData = {
  type: ModalType;
  title?: string;
  description?: string;
  primaryButtonText?: string;
  onPrimaryButtonClick?: () => void;
  icon?: IconName;
  iconClassName?: ClassValue;
  customContent?: React.ReactNode;
  modalProps?: Partial<ModalProps>;
  errorLogData?: ErrorLogData;
};

export const ModalContext = createContext<ContextType>({
  isOpen: false,
  data: undefined,
  openModal: noop,
  closeModal: noop,
});
export const AppModalContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation('common');
  const { isOpen, onOpen: openModal, onClose: closeModal, data } = useDisclosure<ModalData>();

  const handlePrimaryButtonClick = () => {
    data?.onPrimaryButtonClick?.();
    closeModal();
  };

  // Both error modals take the same payload and the same fallbacks; only the design differs.
  const errorModalProps = data && {
    open: isOpen,
    title: data.title || t('error.common_error_something_went_wrong_text'),
    description: data.description || t('error.common_error_please_try_again_text'),
    buttonText: data.primaryButtonText || t('error.common_error_button_label_ok'),
    onButtonClick: handlePrimaryButtonClick,
    icon: data.icon,
    iconClassName: data.iconClassName,
    errorLogData: data.errorLogData || {
      action: ActionType.OnPageLoad,
      component: 'AppModalContext',
      description: 'Global error modal displayed',
      context: {
        modalTitle: data.title,
        modalDescription: data.description,
        hasCustomButtonText: !!data.primaryButtonText,
      },
    },
  };

  return (
    <ModalContext.Provider
      value={{
        isOpen,
        data,
        openModal,
        closeModal,
      }}
    >
      {children}
      {isOpen && errorModalProps && data?.type === ModalType.ERROR && <ErrorModal {...errorModalProps} />}
      {isOpen && errorModalProps && data?.type === ModalType.ERROR_REVAMP && <RevampErrorModal {...errorModalProps} />}
      {isOpen && data?.type === ModalType.CUSTOM && (
        <Modal open={isOpen} onClose={closeModal} {...data.modalProps}>
          <Heading variant="h5" className="my-2 flex justify-between">
            {data.title}
            <Button onClick={closeModal} className="flex items-center gap-1">
              <Icon icon={IconName.Close} />
            </Button>
          </Heading>
          {data.customContent}
        </Modal>
      )}
    </ModalContext.Provider>
  );
};

export const useAppModalContext = () => useContext(ModalContext);
